import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle2, ClipboardCheck, RefreshCw, ShieldCheck, GraduationCap, Ban } from 'lucide-react';
import { toast } from 'sonner';
import MeetingLinkEditor from '../../components/staff/MeetingLinkEditor';

const SessionCoordination = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bookings/coordination`);
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (booking, field, value) => {
    // optimistic update
    setItems((prev) => prev.map((b) => b.id === booking.id
      ? { ...b, coordination: { ...b.coordination, [field]: value } } : b));
    try {
      await axios.patch(`${API}/bookings/${booking.id}/coordination`, { [field]: value });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update checklist');
      load();
    }
  };

  const verify = async (booking) => {
    try {
      await axios.post(`${API}/bookings/${booking.id}/coordination/verify`);
      toast.success('Session coordination verified');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not verify');
    }
  };

  const bothDone = (c) => c?.informed_teacher && c?.shared_meeting_link;

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/bookings/${cancelTarget.id}/cancel`, { reason: cancelReason || null });
      toast.success('Booking cancelled. Finance has been notified to process any refund.');
      setCancelTarget(null);
      setCancelReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const refundBadge = (b) => {
    if (b.refund_status === 'pending') return <Badge className="bg-amber-100 text-amber-700 border-amber-300">Refund pending</Badge>;
    if (b.refund_status === 'refunded') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Refunded</Badge>;
    return null;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="session-coordination-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-violet-600" /> Session Coordination
          </h1>
          <p className="text-slate-600">
            {['director', 'admin'].includes(user.role)
              ? 'Verify that executives have coordinated each booked session.'
              : 'Complete the checklist for each of your students\' bookings.'}
          </p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full" data-testid="refresh-coordination-button">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500">Loading...</div>
      ) : items.length === 0 ? (
        <Card className="border-0 shadow">
          <CardContent className="py-16 text-center" data-testid="no-coordination-items">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-slate-600">No sessions need coordination right now.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((b) => (
            <Card key={b.id} className="border-0 shadow-lg" data-testid={`coordination-card-${b.id}`}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-violet-600" />
                      <span data-testid={`coordination-student-${b.id}`}>{b.student_name}</span>
                    </CardTitle>
                    <p className="text-sm text-slate-600 mt-1">
                      {b.subject} with <span className="font-medium">{b.teacher_name}</span>
                    </p>
                    <p className="text-sm text-slate-500">
                      {new Date(b.booking_date).toLocaleDateString()} • {b.start_time} • {b.duration_hours}h
                      {b.executive_name && <> • Executive: <span className="font-medium">{b.executive_name}</span></>}
                    </p>
                    {(b.group_size || 1) > 1 && (
                      <p className="text-sm text-violet-700 font-medium" data-testid={`coordination-group-${b.id}`}>
                        Group of {b.group_size} · host + {(b.participants || []).length} joined · {b.group_size - 1 - (b.participants || []).length} open
                        {(b.participants || []).length > 0 && ` (${b.participants.map((p) => p.name).join(', ')})`}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {b.booking_status === 'cancelled' ? (
                      <>
                        <Badge className="bg-rose-100 text-rose-700 border-rose-300" data-testid={`coordination-cancelled-badge-${b.id}`}>
                          <Ban className="w-3.5 h-3.5 mr-1" /> Cancelled
                        </Badge>
                        {refundBadge(b)}
                      </>
                    ) : b.coordination_verified ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300" data-testid={`coordination-verified-badge-${b.id}`}>
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Verified
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300">Pending</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 bg-slate-50 rounded-xl p-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={!!b.coordination?.informed_teacher}
                      disabled={!b.can_edit || b.coordination_verified || b.booking_status === 'cancelled'}
                      onCheckedChange={(v) => toggle(b, 'informed_teacher', !!v)}
                      data-testid={`check-informed-teacher-${b.id}`}
                    />
                    <span className={`text-sm ${b.coordination?.informed_teacher ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                      Informed the teacher?
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={!!b.coordination?.shared_meeting_link}
                      disabled={!b.can_edit || b.coordination_verified || b.booking_status === 'cancelled'}
                      onCheckedChange={(v) => toggle(b, 'shared_meeting_link', !!v)}
                      data-testid={`check-shared-link-${b.id}`}
                    />
                    <span className={`text-sm ${b.coordination?.shared_meeting_link ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                      Shared the meeting link with the teacher and student?
                    </span>
                  </label>
                  <MeetingLinkEditor booking={b} disabled={!b.can_edit || b.booking_status === 'cancelled'} onSaved={load} />
                </div>

                {(b.can_cancel || (b.can_verify && !b.coordination_verified && b.booking_status !== 'cancelled')) && (
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    {b.can_cancel && (
                      <Button
                        variant="outline"
                        onClick={() => { setCancelTarget(b); setCancelReason(''); }}
                        className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50"
                        data-testid={`cancel-booking-${b.id}`}
                      >
                        <Ban className="w-4 h-4 mr-2" /> Cancel Booking
                      </Button>
                    )}
                    {b.can_verify && !b.coordination_verified && b.booking_status !== 'cancelled' && (
                      <Button
                        onClick={() => verify(b)}
                        disabled={!bothDone(b.coordination)}
                        className="bg-violet-600 hover:bg-violet-700 text-white rounded-full disabled:opacity-50"
                        data-testid={`verify-coordination-${b.id}`}
                      >
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        {bothDone(b.coordination) ? 'Verify Coordination' : 'Awaiting checklist'}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!cancelTarget} onOpenChange={(v) => { if (!v) setCancelTarget(null); }}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this booking?</DialogTitle>
            <DialogDescription>
              {cancelTarget?.student_name}'s session ({cancelTarget?.subject} with {cancelTarget?.teacher_name}) will be cancelled.
              If it was paid, Finance will be notified to process the refund.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="cancel-reason">Reason (optional)</Label>
            <Textarea
              id="cancel-reason"
              placeholder="e.g. Student requested cancellation"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              data-testid="cancel-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)} className="rounded-full">Keep Booking</Button>
            <Button
              onClick={confirmCancel}
              disabled={cancelling}
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-full"
              data-testid="confirm-cancel-button"
            >
              {cancelling ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SessionCoordination;
